import { useState, useCallback, useEffect, useRef } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DictationError,
  dictationMessageKey,
  isRetryable,
  newUpload,
  transcribe,
  type DictationErrorCode,
} from '@arthelper/shared/dictation';

const AUDIO_TRANSCRIPTION_LAMBDA_URL =
  import.meta.env.VITE_AUDIO_TRANSCRIPTION_LAMBDA_URL ||
  (import.meta.env.DEV ? `${__MOCKS_LAMBDA_URL__}/audio-transcription` : undefined);

const GENERATE_SIGNED_URL = gql`
  mutation GenerateSignedUrl($input: SignedUrlInput!) {
    generateSignedUrl(input: $input)
  }
`;

/** A stop this soon after start is a cancel; longer than this, empty audio is a failure. */
const CANCEL_WINDOW_MS = 500;

function classify(error: unknown): DictationErrorCode {
  if (error instanceof DictationError) {
    return error.code;
  }
  return 'failed';
}

function classifyCapture(error: unknown): DictationErrorCode {
  const name = error instanceof DOMException ? error.name : undefined;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'permission-denied';
  }
  // Safari refuses the opus mime type outright: another tap cannot help.
  if (name === 'NotSupportedError') {
    return 'unsupported';
  }
  return 'failed';
}

interface UseAudioTranscriptionProps {
  onTranscriptionComplete: (text: string, appendText?: boolean) => void;
  appendText?: boolean;
}

// Custom audio recorder hook compatible with React 19
function useCustomAudioRecorder(onError: (code: DictationErrorCode) => void, onStart: () => void) {
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startingRef = useRef(false);
  const pendingStopRef = useRef(false);
  const mountedRef = useRef(true);
  const startedAtRef = useRef(0);

  const releaseStream = useCallback(() => {
    if (!streamRef.current) {
      return;
    }
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    // Both taps land on one button, so a tap that reaches this while a recording is
    // already live means the isRecording flag lied -- stop, rather than start a second
    // recording that would orphan the first stream.
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      return;
    }

    // Still waiting on getUserMedia: queue the stop for the tail of this call.
    if (startingRef.current) {
      pendingStopRef.current = true;
      return;
    }

    onStart();

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices) {
      onError('unsupported');
      return;
    }

    // This attempt owns the blob from here on, so a capture failure can never leave the
    // previous take behind for a retry to re-send.
    setRecordingBlob(null);
    startingRef.current = true;
    pendingStopRef.current = false;
    setIsStarting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // The host can unmount while the permission prompt is open. Nothing would ever
      // release a stream handed to a hook that is already gone.
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      mediaRecorderRef.current = mediaRecorder;

      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setIsRecording(false);
        releaseStream();

        if (blob.size > 0) {
          setRecordingBlob(blob);
          return;
        }

        // Nothing was captured. Tapped straight through, that is a cancel and publishing
        // an empty blob would report a failure nobody caused. Held open and still empty,
        // the capture itself broke -- a dead microphone mid-take -- and silence there is
        // the defect this whole change exists to remove.
        if (Date.now() - startedAtRef.current < CANCEL_WINDOW_MS) {
          return;
        }

        onError('failed');
      };

      startedAtRef.current = Date.now();
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      // The stream is already live when the recorder itself refuses the mime type.
      releaseStream();

      // A prompt answered after the host unmounted must not toast on whatever screen the
      // artist is looking at now.
      if (!mountedRef.current) {
        return;
      }

      onError(classifyCapture(error));
    } finally {
      startingRef.current = false;
      setIsStarting(false);
    }

    // Honour a stop tapped during startup, so the recorder never keeps running behind a
    // button that already reads "stop".
    const stopRequested = pendingStopRef.current;
    pendingStopRef.current = false;

    if (stopRequested && mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, [onError, onStart, releaseStream]);

  // Guard on the recorder's own state, not the React flag: isRecording flips in a
  // later render, so a stop tapped in the same interaction as start would be a no-op.
  const stopRecording = useCallback(() => {
    // A live recorder wins over the startup flag: the flag can still be set for the
    // instant between the recorder starting and this call returning, and a stop dropped
    // in that window is the silent no-op this whole change exists to remove.
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      return;
    }

    if (startingRef.current) {
      pendingStopRef.current = true;
    }
  }, []);

  const clearRecording = useCallback(() => {
    setRecordingBlob(null);
  }, []);

  // Unmounting while recording -- navigating off an onboarding step -- would otherwise
  // leave the tracks live and the browser's recording indicator lit for the tab's life.
  useEffect(() => {
    // Re-armed here, not just declared: StrictMode runs this cleanup once on mount, and a
    // flag left false would abort every later recording the moment getUserMedia resolves.
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      const recorder = mediaRecorderRef.current;

      if (recorder?.state === 'recording') {
        recorder.onstop = null;
        recorder.stop();
      }

      releaseStream();
    };
  }, [releaseStream]);

  return {
    isRecording,
    isStarting,
    recordingBlob,
    mediaRecorder: mediaRecorderRef.current,
    startRecording,
    stopRecording,
    clearRecording,
  };
}

export function useAudioTranscription({ onTranscriptionComplete, appendText = false }: UseAudioTranscriptionProps) {
  const { t } = useTranslation('chat');
  const { getToken } = useClerkAuth();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<DictationErrorCode | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [generateSignedUrl] = useMutation(GENERATE_SIGNED_URL);
  const processedBlobRef = useRef<Blob | null>(null);
  // Bumped by retry() so the upload effect re-runs on purpose, not because the recorder
  // happens to return a fresh object every render.
  const [attempt, setAttempt] = useState(0);
  // What survives this take across its retries: the S3 object a successful PUT already
  // produced, so a retry re-posts that URL instead of uploading the same audio again,
  // and whether the last POST was refused as 401, so the next token skips Clerk's cache.
  const uploadRef = useRef(newUpload());

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Single classification point: every failure lands here, and the toast fires once for
  // all surfaces so components only have to render the inline status line.
  const fail = useCallback(
    (code: DictationErrorCode) => {
      setError(code);
      if (!isRetryable(code)) {
        // The service itself is refusing. Latch for the rest of the session so the same
        // refusal cannot be re-triggered. In memory only: a reload re-arms the mic.
        setIsBlocked(true);
      }
      toast.error(t(dictationMessageKey(code)));
    },
    [t]
  );

  // A fresh recording starts clean: no stale message, and no stale upload to re-post.
  const startAttempt = useCallback(() => {
    uploadRef.current = newUpload();
    clearError();
  }, [clearError]);

  const recorder = useCustomAudioRecorder(fail, startAttempt);

  const handleStopRecording = useCallback(() => {
    recorder.stopRecording();
  }, [recorder]);

  const canRetry = error !== null && isRetryable(error) && !isTranscribing;

  // User-only. Nothing calls this from a timer or from the catch below — an automatic
  // retry would hammer a failing backend.
  const retry = useCallback(() => {
    if (!canRetry) {
      return;
    }

    // Nothing was captured -- a denied, missing or busy microphone never produced a blob
    // -- so the only thing to retry is the recording itself.
    if (!recorder.recordingBlob) {
      recorder.startRecording();
      return;
    }

    // A blob exists: un-stamp it so the upload effect fires exactly once more. `attempt`
    // is what re-runs that effect -- `error` is not one of its dependencies -- so the
    // bump is the mechanism and clearing the error only takes the message off screen.
    processedBlobRef.current = null;
    clearError();
    setAttempt((count) => count + 1);
  }, [canRetry, recorder, clearError]);

  useEffect(() => {
    async function putAudio(blob: Blob) {
      const timestamp = Date.now();

      const { data } = await generateSignedUrl({
        variables: {
          input: {
            fileName: `${timestamp}.${blob.type.split('/')[1]}`,
            fileType: blob.type,
            type: 'private',
            exactPath: `audio/transcriptions/${timestamp}`,
          },
        },
      });

      if (!data?.generateSignedUrl) {
        throw new DictationError('failed', 'Failed to get signed URL');
      }

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.generateSignedUrl);
      xhr.setRequestHeader('Content-Type', blob.type);

      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status !== 200) {
            reject(new DictationError('failed', `Upload failed: ${xhr.status}`));
            return;
          }
          resolve();
        };
        xhr.onerror = () => reject(new DictationError('failed', 'Upload failed'));
        xhr.send(blob);
      });

      return data.generateSignedUrl.split('?')[0];
    }

    async function uploadAudio() {
      try {
        setIsTranscribing(true);
        const blob = recorder.recordingBlob;
        if (!blob) return;

        if (!AUDIO_TRANSCRIPTION_LAMBDA_URL) {
          throw new DictationError('unavailable', 'Transcription endpoint is not configured');
        }

        const text = await transcribe(uploadRef.current, {
          endpoint: AUDIO_TRANSCRIPTION_LAMBDA_URL,
          audio: blob,
          getToken,
          upload: putAudio,
          post: (endpoint, init) => fetch(endpoint, init),
        });

        onTranscriptionComplete(text, appendText);
        recorder.clearRecording();
        uploadRef.current = newUpload();
        clearError();
      } catch (error) {
        console.error('Error processing audio:', error);
        fail(classify(error));
      } finally {
        setIsTranscribing(false);
      }
    }

    if (recorder.recordingBlob && !isTranscribing && recorder.recordingBlob !== processedBlobRef.current) {
      processedBlobRef.current = recorder.recordingBlob;
      uploadAudio();
    }
  }, [
    recorder.recordingBlob,
    isTranscribing,
    generateSignedUrl,
    onTranscriptionComplete,
    recorder,
    appendText,
    fail,
    clearError,
    getToken,
    attempt,
  ]);

  return {
    recorder,
    isTranscribing,
    error,
    isBlocked,
    canRetry,
    retry,
    clearError,
    handleStopRecording,
  };
}
